// Shared job-type + location constants used across Leads / Quotes / Jobs forms.
import {
  Car, Ship, Home, Snowflake, Bike, Truck, Wrench,
  Caravan, Building2, Tag, FileText,
} from "lucide-react";

/**
 * Master list of item types the shop installs on.
 * These are the OBJECTS being worked on — the installation service (vinyl
 * wrap colour change, PPF, chrome delete, etc.) is a separate `installation_type`
 * dropdown on each job_item.
 * `hasDedicatedForm` is legacy; the item form now picks its layout from
 * the item's `installation_type` — see `installationFormKind()`.
 */
export const JOB_TYPES = [
  { value: "car",              label: "Car / Vehicle",     itemLabel: "Vehicle",              icon: Car,      placeholder: "e.g. 2023 Tesla Model 3", hasDedicatedForm: true },
  { value: "boat",             label: "Boat",              itemLabel: "Boat",                 icon: Ship,     placeholder: "e.g. Sea Ray 21ft" },
  { value: "caravan",          label: "Caravan / RV",      itemLabel: "Caravan / RV",         icon: Caravan,  placeholder: "e.g. Jayco Silverline 21ft" },
  { value: "motorhome",        label: "Motorhome",         itemLabel: "Motorhome",            icon: Caravan,  placeholder: "e.g. Winnebago Vita" },
  { value: "motorbike",        label: "Motorbike",         itemLabel: "Motorbike",            icon: Bike,     placeholder: "e.g. 2022 Ducati Panigale V4" },
  { value: "jetski",           label: "Jetski",            itemLabel: "Jetski",               icon: Ship,     placeholder: "e.g. 2023 Sea-Doo RXT-X 300" },
  { value: "commercial",       label: "Commercial fleet",  itemLabel: "Commercial vehicle",   icon: Truck,    placeholder: "e.g. Ford Transit van #3" },
  { value: "van",              label: "Van",               itemLabel: "Van",                  icon: Truck,    placeholder: "e.g. 2022 Mercedes Sprinter LWB" },
  { value: "truck",            label: "Truck",             itemLabel: "Truck",                icon: Truck,    placeholder: "e.g. 2023 Isuzu NPR 200 tray" },
  { value: "appliance",        label: "Appliance",         itemLabel: "Appliance",            icon: Snowflake, placeholder: "e.g. Samsung double-door fridge" },
  { value: "fridge",           label: "Fridge",            itemLabel: "Fridge",               icon: Snowflake, placeholder: "e.g. LG 500L stainless" },
  { value: "building_interior", label: "Building Interior", itemLabel: "Building interior",    icon: Home,     placeholder: "e.g. lobby feature wall" },
  { value: "building_exterior", label: "Building Exterior", itemLabel: "Building exterior",    icon: Building2, placeholder: "e.g. shopfront cladding" },
  { value: "signage",          label: "Signage",           itemLabel: "Signage",              icon: Tag,      placeholder: "e.g. 3m × 1m illuminated panel" },
  { value: "printing",         label: "Printing / Plotting", itemLabel: "Print job",           icon: FileText, placeholder: "e.g. 500× A5 stickers on white gloss" },
  { value: "other",            label: "Other",             itemLabel: "Item description",     icon: Wrench,   placeholder: "Describe the item" },
];

export const jobTypeMeta = (value) =>
  JOB_TYPES.find((t) => t.value === value) || JOB_TYPES[0];

/**
 * Vehicle-style job types collect Year / Make / Model / Condition on the
 * job card. Everything else (appliance, fridge, signage, other, buildings)
 * uses a simple "Item description" free-text box instead — Year/Make/Model
 * isn't meaningful for a fridge or a shopfront.
 */
const VEHICLE_JOB_TYPES = new Set([
  "car", "boat", "caravan", "motorhome", "motorbike", "commercial", "jetski",
  "van", "truck",
]);

export const isVehicleJobType = (value) => VEHICLE_JOB_TYPES.has(value);

export const LOCATION_TYPES = [
  { value: "on_site",  label: "On-site (in shop)" },
  { value: "off_site", label: "Off-site (customer location)" },
];

// ---------------------------------------------------------------------------
// Installation types — WHAT the shop is doing to the item. Item type (car,
// boat, building etc.) is separate. `form_kind` drives which per-item form
// layout renders inside <JobItemForm />: "wrap" (year/make/model + wrap
// areas), "ppf" (year/make/model + ppf areas/brand/thickness), or "notes"
// (single free-form textarea).
// ---------------------------------------------------------------------------

export const INSTALLATION_TYPES = [
  { value: "wrap_colour_change",   label: "Vinyl Wrap — Full Colour Change", form_kind: "wrap" },
  { value: "wrap_business",        label: "Vinyl Wrap — Print / Branding",   form_kind: "wrap" },
  { value: "wrap_interior",        label: "Vinyl Wrap — Interior",           form_kind: "wrap" },
  { value: "wrap_partial",         label: "Vinyl Wrap — Partial",            form_kind: "wrap" },
  { value: "wrap_chrome_delete",   label: "Chrome Delete",                   form_kind: "wrap" },
  { value: "ppf_partial",          label: "PPF — Partial",                   form_kind: "ppf" },
  { value: "ppf_full_front",       label: "PPF — Front Coverage",            form_kind: "ppf" },
  { value: "ppf_full_body",        label: "PPF — Full Body",                 form_kind: "ppf" },
  { value: "ppf_colour",           label: "Colour PPF — Full Body",          form_kind: "ppf" },
  { value: "ppf_colour_partial",   label: "Colour PPF — Partial",            form_kind: "ppf" },
  { value: "tinting",              label: "Window Tinting",                  form_kind: "tint" },
  { value: "ceramic",              label: "Ceramic Coating",                 form_kind: "notes" },
  // Powder coating — baked-on polyester finish usually applied to wheels
  // but shops also take it on for small parts (brake calipers, roof
  // racks, grille slats, bike frames). Two entries so quotes/invoices
  // read specifically "Powder Coating — Wheels" vs "Powder Coating —
  // Other" without the office having to type it manually every time.
  { value: "powder_coat_wheels",   label: "Powder Coating — Wheels",         form_kind: "notes" },
  { value: "powder_coat_other",    label: "Powder Coating — Other",          form_kind: "notes" },
  // Detailing services — grouped together for easy pickup in the office.
  // Full covers the whole car (inside + outside), Exterior is a machine-
  // polish/decontamination pass, Interior is deep clean of cabin/boot,
  // Paint Correction is the multi-stage cut/polish (pre-ceramic prep),
  // Engine Bay is degrease + dress, Headlight Restoration is the
  // sand-and-clear-coat treatment.
  { value: "detail_full",          label: "Detail — Full Vehicle",           form_kind: "notes" },
  { value: "detail_exterior",      label: "Detail — Exterior",               form_kind: "notes" },
  { value: "detail_interior",      label: "Detail — Interior",               form_kind: "notes" },
  { value: "detail_paint_correction", label: "Detail — Paint Correction",    form_kind: "notes" },
  { value: "detail_engine_bay",    label: "Detail — Engine Bay",             form_kind: "notes" },
  { value: "detail_headlight_restoration", label: "Detail — Headlight Restoration", form_kind: "notes" },
  { value: "signage_decals",       label: "Signage / Decals",                form_kind: "notes" },
  // Body kit installation — bolt-on aero (front lips, side skirts,
  // widebody kits). `default_off: true` keeps it hidden from the picker
  // for shops that don't fit body kits until they tick it on in
  // Settings → Services offered.
  { value: "body_kit_install",     label: "Body kit installation",           form_kind: "notes", default_off: true },
  // Lens tint — headlight + taillight smoke/darkening film. One combined
  // installation type; the shop then picks a specific application area
  // (full vehicle set, headlight only, taillight only, etc.) via the
  // dedicated `lens_tint` sub-form.
  { value: "lens_tint",            label: "Headlight / Taillight Tint",      form_kind: "lens_tint" },
  // Badge modification — bundles every "do something to the badges" job
  // (deletes / blackouts / swaps / custom emblems / decal replacements)
  // under one installation type. The shop then picks the specific pieces
  // of work required via a multi-select on the form so quotes/invoices
  // read e.g. "Badge modification — Badge delete + Badge blackout".
  { value: "badge_modification",   label: "Badge Modification",              form_kind: "badge" },
  // Print / plot only — customer supplies design or brief and just wants
  // physical output. Feeds the "printing" job type but also usable
  // standalone from any job (e.g. bundling stickers with a wrap).
  { value: "print_only",           label: "Print only (customer applies)",   form_kind: "notes" },
  { value: "plot_only",            label: "Plot / cut only (vinyl decals)",  form_kind: "notes" },
  { value: "print_and_plot",       label: "Print + plot (contour cut)",      form_kind: "notes" },
  { value: "large_format_print",   label: "Large-format print (banner/backdrop)", form_kind: "notes" },
  { value: "vehicle_decals",       label: "Vehicle decals / lettering",      form_kind: "notes" },
  { value: "other",                label: "Other (specify in notes)",        form_kind: "notes" },
];

export const installationMeta = (value) => {
  const direct = INSTALLATION_TYPES.find((t) => t.value === value);
  if (direct) return direct;
  // Legacy compat: quotes/jobs created before the three lens-tint types
  // were unified still store `headlight_tint` / `taillight_tint` /
  // `lens_tint_full_set`. Resolve them to the new `lens_tint` meta so
  // the form + labels render properly without a data migration.
  if (LEGACY_INSTALLATION_TYPES[value]) {
    return { ...INSTALLATION_TYPES.find((t) => t.value === "lens_tint"), value };
  }
  return null;
};

/**
 * Application-area options for the combined "Headlight / Taillight Tint"
 * installation. Kept small + fixed — shops that do custom lens work can
 * always type into the installation notes field.
 */
export const LENS_TINT_AREAS = [
  { value: "full_vehicle",         label: "Full vehicle tint" },
  { value: "headlight",            label: "Headlight" },
  { value: "taillight",            label: "Taillight" },
  { value: "taillight_reflectors", label: "Taillight + Reflectors" },
  { value: "reflectors",           label: "Reflectors" },
];

export const lensTintAreaLabel = (value) =>
  (LENS_TINT_AREAS.find((a) => a.value === value) || {}).label || "";

/**
 * Multi-select options for the "Badge Modification" installation. Unlike
 * lens tint (where the areas are mutually exclusive), badge work usually
 * stacks — a single job might Delete the rear badge, Blackout the side
 * badges and Replace the front emblem. Stored on `installation.badge_specifics`
 * as a string array.
 */
export const BADGE_SPECIFICS = [
  { value: "badge_delete",       label: "Badge delete" },
  { value: "badge_blackout",     label: "Badge blackout" },
  { value: "badge_replace",      label: "Badge replace" },
  { value: "custom_badge",       label: "Custom badge" },
  { value: "sticker_replacement", label: "Sticker replacement" },
];

export const badgeSpecificLabel = (value) =>
  (BADGE_SPECIFICS.find((b) => b.value === value) || {}).label || "";

/**
 * Legacy installation types that were retired when the lens-tint family
 * was unified. Maps the old value → (label, default_area) so any older
 * quote/job renders correctly and can be opened for edit without losing
 * intent. `default_area` is applied by the form when the legacy value is
 * upgraded to `lens_tint` on first save.
 */
export const LEGACY_INSTALLATION_TYPES = {
  headlight_tint:     { label: "Headlight lens tint",      default_area: "headlight" },
  taillight_tint:     { label: "Taillight lens tint",      default_area: "taillight" },
  lens_tint_full_set: { label: "Lens tint — full set",     default_area: "full_vehicle" },
};

/**
 * Enabled-services filter. Admin can tick which services their shop
 * offers in Settings → Services offered; unticked services stay in the
 * master list (so old jobs still render their labels) but drop out of
 * the picker dropdowns for less clutter.
 *
 * The enabled list is fetched from `/api/settings/enabled-services` on
 * app boot and cached in `window.__enabledServices`. `null` = every
 * service enabled (default for a fresh install).
 */
function _enabledSet(kind) {
  if (typeof window === "undefined") return null;
  const cache = window.__enabledServices;
  if (!cache) return null;
  const arr = cache[kind];
  if (!Array.isArray(arr) || arr.length === 0) return null;
  return new Set(arr);
}

export const enabledJobTypes = () => {
  const s = _enabledSet("job_types");
  if (s) return JOB_TYPES.filter((t) => s.has(t.value));
  // No admin preference yet — hide any master-list entry flagged
  // `default_off` so opt-in services stay hidden on a fresh install.
  return JOB_TYPES.filter((t) => !t.default_off);
};

export const enabledInstallationTypes = () => {
  const s = _enabledSet("installation_types");
  if (s) return INSTALLATION_TYPES.filter((t) => s.has(t.value));
  return INSTALLATION_TYPES.filter((t) => !t.default_off);
};


/**
 * Given a job_item, decide which dedicated form to render. Prefers the new
 * `installation_type`; falls back to the legacy `job_type` heuristic so
 * pre-existing items (created before the split) still render sensibly.
 */
export function installationFormKind(item) {
  if (!item) return "notes";
  // New shape — pick from the first installation
  const inst = (item.installations && item.installations[0]) || null;
  const installType = inst?.installation_type || item.installation_type;
  if (installType) {
    const meta = installationMeta(installType);
    if (meta) return meta.form_kind;
  }
  // Legacy fallback
  if (item.job_type === "ppf") return "ppf";
  if (item.job_type === "car" || item.job_type === "motorbike" || item.job_type === "commercial") return "wrap";
  return "notes";
}

// ---------------------------------------------------------------------------
// Per-item field schemas — used by <JobItemForm /> to render the right
// dedicated form for Vinyl Wrap ("car"/"motorbike"/"commercial") vs PPF vs
// everything else. `car` gets the full vinyl wrap form. All other vehicle
// types share the same fields but only "car" & "ppf" are marked hasDedicatedForm.
// ---------------------------------------------------------------------------

export const VINYL_WRAP_AREAS = [
  "Full Coverage",
  "Roof",
  "Bonnet",
  "Boot",
  "Front bumper",
  "Rear bumper",
  "Doors",
  "Fenders",
  "Mirrors",
  "Door handles",
  "A-pillars",
  "B-pillars",
  "C-pillars",
  "Wings",
  "Tub",
  "Wheels",
  "Custom accents",
];

export const PPF_AREAS = [
  "Full front (bumper, bonnet, fenders, mirrors)",
  "Partial front (bumper + partial bonnet)",
  "Full body",
  "Front bumper only",
  "Bonnet only",
  "Roof",
  "Fenders",
  "Wings",
  "Rear bumper",
  "A-pillars",
  "B-pillars",
  "C-pillars",
  "Tub",
  "Door edges & handle cups",
  // Interior PPF — piano-black trim, screens and high-touch surfaces are
  // increasingly common requests (Teslas, EVs, Audis). One combined
  // entry; the specific pieces go in the installation notes.
  "Interior — dash, screens, piano-black & door-cards",
  "Custom (specify in notes)",
];

// ---------------------------------------------------------------------------
// Vehicle-type-aware area catalogues. The old flat VINYL_WRAP_AREAS / PPF_AREAS
// arrays above stay exported for backwards-compat with any older callers,
// but every consumer that knows the parent item's `job_type` should use
// `wrapAreasFor(jobType)` / `ppfAreasFor(jobType)` so a motorbike quote
// picks from "Front fairing / Tank / Tail fairing" instead of "Bonnet / Boot".
// ---------------------------------------------------------------------------

const WRAP_AREAS_BY_TYPE = {
  car: VINYL_WRAP_AREAS,
  commercial: [
    "Full Coverage",
    "Cab roof",
    "Bonnet",
    "Front bumper",
    "Rear bumper / tailgate",
    "Doors (both sides)",
    "Rear cargo doors",
    "Side panels / bodysides",
    "Fenders",
    "Mirrors",
    "Wheel arches",
    "Sign-writing panels",
    "Custom accents",
  ],
  van: [
    "Full Coverage",
    "Roof",
    "Bonnet",
    "Front bumper",
    "Rear cargo doors",
    "Sliding side door",
    "Driver / passenger doors",
    "Side panels / bodysides",
    "Fenders",
    "Mirrors",
    "Wheel arches",
    "Rear windows blackout",
    "Sign-writing panels",
    "Custom accents",
  ],
  truck: [
    "Full Coverage",
    "Cab roof",
    "Bonnet",
    "Front bumper",
    "Cab doors",
    "Cab side panels",
    "Sleeper cab",
    "Tray / tub sides",
    "Tailgate",
    "Rear bumper",
    "Fenders",
    "Mirrors",
    "Fuel tank covers",
    "Wheel arches",
    "Sign-writing panels",
    "Custom accents",
  ],
  motorbike: [
    "All Fairings",
    "Front fairing / nose",
    "Upper fairings",
    "Lower fairings / belly pan",
    "Fuel tank",
    "Tank pads",
    "Side panels",
    "Tail fairing / seat cowl",
    "Front fender / mudguard",
    "Rear fender / hugger",
    "Swingarm",
    "Fork lowers",
    "Wheels / rims",
    "Mirrors",
    "Windscreen surround",
    "Number boards",
    "Custom accents",
  ],
  jetski: [
    "Full hull wrap",
    "Bow / nose cone",
    "Deck / top",
    "Hull sides (port & starboard)",
    "Bond rails",
    "Rear boarding step",
    "Handlebars / helm cowl",
    "Storage hatches",
    "Foot wells",
    "Seat panels",
    "Trailer",
    "Custom accents",
  ],
  boat: [
    "Hull sides (port & starboard)",
    "Transom",
    "Bow",
    "Waterline stripe",
    "Cabin sides",
    "Cabin roof / hard-top",
    "Deck accents",
    "Console / dash",
    "Name / registration decals",
    "Trailer",
    "Custom accents",
  ],
  caravan: [
    "Full Coverage",
    "Front panel",
    "Rear panel",
    "Roof",
    "Both sides",
    "Door panel",
    "Storage hatches",
    "Wheel arches",
    "Sign-writing / logos",
    "Custom accents",
  ],
  motorhome: [
    "Full Coverage",
    "Cab (bonnet + doors + bumper)",
    "Front panel",
    "Rear panel",
    "Roof",
    "Both sides",
    "Storage hatches",
    "Wheel arches",
    "Sign-writing / logos",
    "Custom accents",
  ],
  appliance: [
    "Front panel",
    "Doors",
    "Side panels",
    "Top",
    "Full wrap",
    "Custom accents",
  ],
  fridge: [
    "Doors",
    "Side panels",
    "Top",
    "Full wrap",
    "Custom accents",
  ],
  building_interior: [
    "Feature wall",
    "Reception counter",
    "Meeting room walls",
    "Column wraps",
    "Doors",
    "Glass frosting",
    "Ceiling accents",
    "Custom (specify in notes)",
  ],
  building_exterior: [
    "Shopfront cladding",
    "Signage panels",
    "Window graphics",
    "Awning",
    "Pillars / columns",
    "Custom (specify in notes)",
  ],
  signage: [
    "Full face",
    "Return / edges",
    "Vinyl cut lettering",
    "Digital print",
    "Custom (specify in notes)",
  ],
  other: [
    "Full wrap",
    "Partial wrap",
    "Custom (specify in notes)",
  ],
};

const PPF_AREAS_BY_TYPE = {
  car: PPF_AREAS,
  commercial: [
    "Full front (bumper, bonnet, fenders, mirrors)",
    "Full body",
    "Front bumper only",
    "Bonnet only",
    "Rock-chip zones (leading edges)",
    "Door edges & handle cups",
    "High-wear cargo panels",
    "Custom (specify in notes)",
  ],
  van: [
    "Full front (bumper, bonnet, fenders, mirrors)",
    "Front bumper only",
    "Bonnet leading edge",
    "Rock-chip zones (leading edges)",
    "Sliding door track & sill",
    "Rear cargo door edges",
    "Door edges & handle cups",
    "High-wear cargo panels",
    "Custom (specify in notes)",
  ],
  truck: [
    "Full front (bumper, bonnet, fenders, mirrors)",
    "Front bumper only",
    "Bonnet & fender stone-chip zone",
    "Rock-chip zones (leading edges)",
    "Cab door edges & handle cups",
    "Tray / tub leading edges",
    "Tailgate top strip",
    "High-wear touchpoints",
    "Custom (specify in notes)",
  ],
  motorbike: [
    "Full front fairing",
    "Fuel tank",
    "Tank sides only",
    "Tail fairing",
    "Front fender",
    "Rear fender",
    "Belly pan",
    "Headlight / instrument cluster",
    "High-wear touchpoints (bars, levers)",
    "Custom (specify in notes)",
  ],
  jetski: [
    "Bow / nose (impact zone)",
    "Deck / top (foot & board wear)",
    "Hull sides scuff zones",
    "Bond rails",
    "Rear boarding step",
    "Handlebar / helm cowl",
    "High-wear touchpoints",
    "Custom (specify in notes)",
  ],
  boat: [
    "Bow leading edge",
    "Hull scuff zones",
    "Cabin high-wear areas",
    "Windscreen",
    "Custom (specify in notes)",
  ],
  caravan: [
    "Front panel (stone-chip zone)",
    "Front stone-guard strip",
    "Leading edges",
    "Wheel arches",
    "Custom (specify in notes)",
  ],
  motorhome: [
    "Front cab (stone-chip zone)",
    "Front stone-guard strip",
    "Leading edges",
    "Wheel arches",
    "Custom (specify in notes)",
  ],
  appliance: [
    "High-touch surfaces",
    "Custom (specify in notes)",
  ],
  fridge: [
    "High-touch surfaces",
    "Custom (specify in notes)",
  ],
  other: [
    "High-wear surfaces",
    "Custom (specify in notes)",
  ],
};

/**
 * Resolve the correct wrap-area catalogue for a job_type. Falls back to
 * the flat VINYL_WRAP_AREAS when the type isn't known (e.g. brand-new
 * job type added on the backend before this map catches up).
 */
export const wrapAreasFor = (jobType) => WRAP_AREAS_BY_TYPE[jobType] || VINYL_WRAP_AREAS;

/**
 * Resolve the correct PPF-area catalogue for a job_type. PPF is uncommon
 * on some item types (buildings, signage) so those explicitly fall back
 * to the generic car list; the office can pick "Custom (specify in notes)"
 * if the type truly doesn't fit.
 */
/**
 * Split a PPF area catalogue into two families — **Bulk** (installer measures
 * + cuts film on the vehicle from a full roll) and **Template** (pre-cut
 * computer template from a plotter) — since the two are quoted, timed and
 * priced differently.
 *
 *  - Every area gets duplicated with a `Bulk - ` prefix followed by a
 *    `Template - ` prefix.
 *  - "Custom (specify in notes)" bypasses the split — it always sits at the
 *    end as a single terminal entry so the office can catch anything not
 *    covered by the catalogue.
 */
const splitBulkTemplate = (list) => {
  const custom = list.filter((a) => a.toLowerCase().startsWith("custom"));
  const rest = list.filter((a) => !a.toLowerCase().startsWith("custom"));
  return [
    ...rest.map((a) => `Bulk - ${a}`),
    ...rest.map((a) => `Template - ${a}`),
    ...custom,
  ];
};

export const ppfAreasFor = (jobType) => splitBulkTemplate(PPF_AREAS_BY_TYPE[jobType] || PPF_AREAS);


// ---------------------------------------------------------------------------
// Window Tinting — windows catalogue, by vehicle type.
// Each entry is a specific pane of glass the office can tick. Keeps the
// quote precise and lets installers see exactly which windows are in scope
// before they start cutting film.
// ---------------------------------------------------------------------------

export const TINT_WINDOW_AREAS = [
  "Windscreen (full)",
  "Windscreen (visor strip)",
  "Driver — front door",
  "Passenger — front door",
  "Driver — rear door",
  "Passenger — rear door",
  "Driver — rear quarter",
  "Passenger — rear quarter",
  "Rear windscreen",
  "Sunroof / Moonroof",
  "Panoramic roof",
];

const TINT_WINDOWS_BY_TYPE = {
  car: TINT_WINDOW_AREAS,
  commercial: [
    "Windscreen (full)",
    "Windscreen (visor strip)",
    "Driver — front door",
    "Passenger — front door",
    "Driver — rear door",
    "Passenger — rear door",
    "Driver — side cargo window",
    "Passenger — side cargo window",
    "Rear cargo doors — left",
    "Rear cargo doors — right",
    "Rear windscreen",
    "Sunroof / Moonroof",
  ],
  van: [
    "Windscreen (full)",
    "Windscreen (visor strip)",
    "Driver — front door",
    "Passenger — front door",
    "Sliding door window",
    "Driver — side cargo window",
    "Passenger — side cargo window",
    "Rear cargo door — left",
    "Rear cargo door — right",
    "Rear quarter windows",
  ],
  truck: [
    "Windscreen (full)",
    "Windscreen (visor strip)",
    "Driver — front door",
    "Passenger — front door",
    "Driver — rear-cab door",
    "Passenger — rear-cab door",
    "Rear cab window",
    "Sunroof / Moonroof",
  ],
  motorbike: [
    // Rare, but a few bike shops do windshield tint
    "Windshield / Fairing screen",
  ],
  boat: [
    "Windscreen",
    "Side cabin windows",
    "Rear cabin windows",
    "Hatch window",
  ],
  rv: [
    "Windscreen",
    "Driver — front door",
    "Passenger — front door",
    "Habitation windows (per side)",
    "Rear windscreen",
    "Skylight / Roof hatch",
  ],
};

/**
 * Resolve the correct window catalogue for a given job_type. Unknown
 * types fall through to the car list — covers "SUV", "ute", and any
 * future types that haven't yet been added to the map.
 */
export const tintWindowsFor = (jobType) => TINT_WINDOWS_BY_TYPE[jobType] || TINT_WINDOW_AREAS;

export const PPF_BRANDS = [
  "XPEL Ultimate Plus",
  "XPEL Stealth (matte)",
  "3M Scotchgard Pro",
  "SunTek Ultra",
  "Llumar Platinum",
  "Teckwrap G75",
  "Teckwrap G85",
  "Teckwrap M75",
  "Other",
];

export const CONDITION_OPTIONS = [
  { value: "new",       label: "Brand new / factory" },
  { value: "excellent", label: "Excellent" },
  { value: "good",      label: "Good" },
  { value: "fair",      label: "Fair — minor imperfections" },
  { value: "poor",      label: "Poor — needs prep" },
];

const uuid = () => (typeof crypto !== "undefined" && crypto.randomUUID)
  ? crypto.randomUUID()
  : `id-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * One installation on a vehicle — e.g. full colour change vinyl wrap. A
 * single job_item can now hold MULTIPLE installations so a customer bringing
 * a car for colour change + window tint doesn't have to retype vehicle
 * details.
 */
export const blankInstallation = () => ({
  id: uuid(),
  installation_type: "",
  price: 0,
  // Vinyl wrap-specific
  wrap_areas: [],
  // PPF-specific
  ppf_areas: [],
  ppf_brand: "",
  // Window-tint specific — list of individual window panes selected.
  // Same shape as wrap_areas / ppf_areas so the backend treats it as a
  // free-form string list without schema changes.
  tint_areas: [],
  // Film / material identifier — free text so the office can note the exact
  // SKU or colour code (e.g. "3M 2080-G12 Deep Black Gloss", "STEK DYNOshield
  // 10 mil"). Only rendered on installation types that use film (wrap/ppf).
  material: "",
  // Business branding design workflow — only meaningful when
  // installation_type === "wrap_business". See jobItemForm for the picker.
  design_source: "",
  design_status: "",
  design_notes: "",
  // Free-form notes per installation
  notes: "",
  // Estimated install time in hours — REQUIRED for auto-quoting so Claude
  // can compute price = base × hourly_rate + materials. Also renders on
  // the job card as a visual guide for installers ("you're 2h into a 6h
  // job") and drives the workshop capacity calculations.
  estimated_hours: 0,
  // Actual hours logged by the installer once work starts. Feeds the
  // job card's hours tracker (estimated vs actual variance) AND the
  // auto-quoter's effective hourly-rate calculation so future quotes
  // learn from what a job REALLY takes to install, not just the estimate.
  actual_hours: 0,
  // Per-installation pipeline stage — moves independently of siblings
  sub_status: "",
  status_history: [],
});

/** Blank job_item template — vehicle-level details + a list of installations. */
export const blankJobItem = (jobType = "car") => ({
  id: uuid(),
  job_type: jobType,
  // Vehicle-level fields (entered ONCE per item)
  year: "",
  make: "",
  model: "",
  condition: "",
  body_kit: false,
  body_kit_notes: "",
  notes: "",
  // Legacy top-level total price on the item — kept as a computed cache;
  // real prices live inside each installation now.
  price: 0,
  // At least one installation to start with
  installations: [blankInstallation()],
});

/**
 * Migration shim — older leads/quotes stored installation_type + areas at
 * the item level. Reads any legacy item into the new {installations:[...]}
 * shape without losing data. Safe to call on already-normalized items.
 */
export function normalizeItem(item) {
  if (!item) return blankJobItem();
  // Already normalized
  if (Array.isArray(item.installations) && item.installations.length > 0) {
    return {
      ...item,
      installations: item.installations.map((inst) => ({
        ...blankInstallation(),
        ...inst,
        id: inst.id || uuid(),
      })),
    };
  }
  // Legacy: pull install-specific fields up into a single installation
  const inst = {
    ...blankInstallation(),
    installation_type: item.installation_type || "",
    price: Number(item.price) || 0,
    wrap_areas: item.wrap_areas || [],
    ppf_areas: item.ppf_areas || [],
    tint_areas: item.tint_areas || [],
    ppf_brand: item.ppf_brand || "",
    design_source: item.design_source || "",
    design_status: item.design_status || "",
    design_notes: item.design_notes || "",
    notes: item.notes || "",
    sub_status: item.sub_status || "",
    status_history: item.status_history || [],
  };
  return {
    id: item.id || uuid(),
    job_type: item.job_type || "car",
    year: item.year || "",
    make: item.make || "",
    model: item.model || "",
    condition: item.condition || "",
    body_kit: !!item.body_kit,
    body_kit_notes: item.body_kit_notes || "",
    notes: "",
    price: Number(item.price) || 0,
    installations: [inst],
  };
}

// ---------------------------------------------------------------------------
// Dynamic pipeline sub-statuses by installation type
// ---------------------------------------------------------------------------
// Every job walks through the base workshop stages. Different installations
// add their own extras — e.g. Business Branding needs design stages, but a
// simple colour change doesn't. The frontend pipeline picker unions the
// extras across all installations on a job so the user only sees the stages
// that are actually relevant to that job.

/** Base stages every job goes through — always visible. */
export const BASE_JOB_STAGES = [
  "unbooked", "booked", "in_store", "wash_down",
  "ready_for_collection", "collected",
];

/** Conditional stages, added when at least one installation on the job needs them.
 *  Keys must EXACTLY match the values in INSTALLATION_TYPES above — misalignment
 *  silently drops stages because Set membership is exact-key. */
export const CONDITIONAL_STAGES_BY_INSTALL = {
  // Business branding — the only install that needs design gates
  wrap_business:      ["awaiting_design", "design_in_progress", "design_approved", "clay_bar", "disassembly", "wrapping", "assembly", "quality_check"],
  // Full colour change / partial / chrome delete / interior — workshop treatment
  wrap_colour_change: ["clay_bar", "disassembly", "wrapping", "assembly", "quality_check"],
  wrap_partial:       ["clay_bar", "disassembly", "wrapping", "assembly", "quality_check"],
  wrap_chrome_delete: ["clay_bar", "disassembly", "wrapping", "assembly", "quality_check"],
  wrap_interior:      ["disassembly", "wrapping", "assembly", "quality_check"],
  // PPF — clay bar prep then quality check, no disassembly / wrapping stage
  ppf_full_front:     ["clay_bar", "quality_check"],
  ppf_full_body:      ["clay_bar", "quality_check"],
  ppf_partial:        ["clay_bar", "quality_check"],
  ppf_colour:         ["clay_bar", "disassembly", "wrapping", "assembly", "quality_check"],
  ppf_colour_partial: ["clay_bar", "disassembly", "wrapping", "assembly", "quality_check"],
  // Tint — light workshop
  tinting:            ["quality_check"],
  // Extras — ceramic needs prep, signage is quality-check only
  ceramic:            ["clay_bar", "quality_check"],
  signage_decals:     ["quality_check"],
  // Detailing — all detail services roll through clay-bar prep and end at
  // quality check. Full & Exterior get the machine polish decontamination
  // handled inside `clay_bar`; Interior is a cabin-only pass so no clay bar.
  detail_full:                   ["clay_bar", "quality_check"],
  detail_exterior:               ["clay_bar", "quality_check"],
  detail_interior:               ["quality_check"],
  detail_paint_correction:       ["clay_bar", "quality_check"],
  detail_engine_bay:             ["quality_check"],
  detail_headlight_restoration:  ["quality_check"],
  // "other" gets nothing extra — just base stages
};

/**
 * Returns the ordered list of sub-status values that should be visible in
 * the pipeline for a given job. Reads all installations on all job_items,
 * unions the conditional stages, and interleaves them into the workshop
 * order so the pipeline still reads left-to-right chronologically.
 */
export function visibleJobSubStatuses(job) {
  // The canonical FULL order (mirrors backend SUB_STATUSES["job"]).
  // Physical order: disassembly comes before clay bar (bare surfaces get
  // better grip on the wrap film).
  const FULL_ORDER = [
    "unbooked", "booked",
    "awaiting_design", "design_in_progress", "design_approved",
    "in_store", "wash_down", "disassembly", "clay_bar",
    "wrapping", "assembly", "quality_check",
    "ready_for_collection", "collected",
  ];
  const items = job?.job_items || [];
  const installs = items.flatMap((it) => (it.installations || (it.installation_type ? [{ installation_type: it.installation_type }] : [])));
  const conditional = new Set();
  for (const inst of installs) {
    const extras = CONDITIONAL_STAGES_BY_INSTALL[inst.installation_type] || [];
    extras.forEach((s) => conditional.add(s));
  }
  const allowed = new Set([...BASE_JOB_STAGES, ...conditional]);
  return FULL_ORDER.filter((s) => allowed.has(s));
}

/**
 * Human-readable, comma-separated list of installation labels present on a job.
 * Used for the "Service type" column on Leads/Quotes tables. Falls back to
 * the legacy top-level `service` string when no items are present.
 */
export function serviceTypesLabel(record) {
  const items = record?.job_items || [];
  const labels = [];
  const seen = new Set();
  for (const it of items) {
    const list = it.installations || (it.installation_type ? [{ installation_type: it.installation_type }] : []);
    for (const inst of list) {
      const meta = installationMeta(inst.installation_type);
      if (meta && !seen.has(meta.value)) {
        labels.push(meta.label);
        seen.add(meta.value);
      }
    }
  }
  if (labels.length === 0) return record?.service || "";
  return labels.join(", ");
}


// ---------------------------------------------------------------------------
// Legacy per-job spec configuration (kept for JobSpecFields component + the
// existing Convert-to-Job modal). New multi-item flow supersedes this for
// leads/quotes but existing screens still consume these.
// ---------------------------------------------------------------------------
export const CUT_METHODS = [
  { value: "plot_cut", label: "Plot cut (pre-cut kit)" },
  { value: "bulk_fit", label: "Bulk fit (roll trimmed on car)" },
];

export const VLT_PRESETS = ["5%", "20%", "35%", "50%", "70%"];

export const TINT_WINDOWS = [
  { value: "windshield",  label: "Windshield" },
  { value: "front_doors", label: "Front doors" },
  { value: "rear_doors",  label: "Rear doors" },
  { value: "rear_window", label: "Rear window" },
  { value: "sunroof",     label: "Sunroof" },
];

export const PPF_FINISHES = [
  { value: "clear", label: "Clear PPF" },
  { value: "color", label: "Color / stealth PPF" },
];

export const PPF_COVERAGE = [
  { value: "full_front",    label: "Full front" },
  { value: "partial_front", label: "Partial front" },
  { value: "full_body",     label: "Full body" },
  { value: "custom",        label: "Custom (specify in notes)" },
];

/** Empty spec skeleton for a given job_type (legacy). */
export const emptySpec = (jobType) => {
  if (jobType === "tinting") {
    return { cut_method: "plot_cut", vlt: "35%", windows: [] };
  }
  if (jobType === "ppf") {
    return { cut_method: "plot_cut", finish: "clear", coverage: [] };
  }
  return {};
};
